import * as THREE from 'three';
import { BlockData, BlockPlanet } from './BlockPlanet';

export class PlanetGenerator {
  private textureLoader: THREE.TextureLoader;
  private baseGeometry: THREE.SphereGeometry;
  private noiseSeed: number = 0;

  constructor() {
    this.textureLoader = new THREE.TextureLoader();
    this.baseGeometry = new THREE.SphereGeometry(1, 32, 32);
  }

  public createPlanet(block: BlockData): BlockPlanet {
    const radius = this.calculateRadius(block.transactionCount);
    const mesh = this.assemblePlanet(block, radius);
    
    return new BlockPlanet(block, mesh, radius);
  }

  private calculateRadius(txCount: number): number {
    // Logarithmic scale: 100 tx = 1.0, 1000 tx = 1.5, 3000 tx = 2.0
    const baseRadius = 0.5;
    const scaleFactor = 0.3;
    return baseRadius + Math.log10(txCount + 1) * scaleFactor;
  }

  private assemblePlanet(block: BlockData, radius: number): THREE.Mesh {
    // Create geometry with appropriate detail
    const geometry = this.baseGeometry.clone();
    geometry.scale(radius, radius, radius);
    
    // Generate unique surface based on block hash
    const material = this.generatePlanetMaterial(block);
    
    // Create mesh
    const mesh = new THREE.Mesh(geometry, material);
    
    // Add surface details using displacement
    this.addSurfaceDetails(mesh, block.hash);
    
    return mesh;
  }

  private generatePlanetMaterial(block: BlockData): THREE.MeshStandardMaterial {
    // Use block hash to generate deterministic colors
    const hash = block.hash || `fallback_${block.height}_${block.timestamp}`;
    
    // Ensure we have enough characters for substring operations
    const paddedHash = hash.length >= 64 ? hash : hash.padEnd(64, '0');
    
    const hue = this.hashToNumber(paddedHash.substring(0, 8), 0, 360);
    const saturation = this.hashToNumber(paddedHash.substring(8, 16), 0.3, 0.7);
    const lightness = this.hashToNumber(paddedHash.substring(16, 24), 0.3, 0.6);
    
    const color = new THREE.Color().setHSL(hue / 360, saturation, lightness);
    
    // Fee-based emission color
    const emissionIntensity = Math.min(1.0, block.totalFees / 1000000); // Normalize to 1 BTC
    const emissionColor = new THREE.Color().setHSL(
      (hue + 180) % 360 / 360, // Complementary color
      0.8,
      0.6
    );
    
    const material = new THREE.MeshStandardMaterial({
      color: color,
      metalness: 0.2 + (block.transactionCount / 3000) * 0.3, // More metallic for busier blocks
      roughness: 0.8 - (block.totalFees / 10000000) * 0.3, // Smoother for high-fee blocks
      emissive: emissionColor,
      emissiveIntensity: emissionIntensity * 0.3,
      envMapIntensity: 0.5
    });
    
    // Generate and apply procedural texture
    const texture = this.generateSurfaceTexture(paddedHash);
    material.map = texture;
    material.bumpMap = texture;
    material.bumpScale = 0.05;
    
    return material;
  }

  private generateSurfaceTexture(hash: string): THREE.Texture {
    const size = 512;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    
    // Ensure hash is defined and has sufficient length
    const safeHash = hash || 'default_hash_for_texture_generation';
    const paddedHash = safeHash.length >= 64 ? safeHash : safeHash.padEnd(64, '0');
    
    // Use hash as seed for procedural generation
    this.noiseSeed = this.hashToNumber(paddedHash, 0, 1000000);
    
    // Create base gradient
    const gradient = ctx.createRadialGradient(size/2, size/2, 0, size/2, size/2, size/2);
    const baseHue = this.hashToNumber(paddedHash.substring(0, 8), 0, 360);
    
    gradient.addColorStop(0, `hsl(${baseHue}, 50%, 60%)`);
    gradient.addColorStop(0.5, `hsl(${(baseHue + 30) % 360}, 40%, 50%)`);
    gradient.addColorStop(1, `hsl(${(baseHue + 60) % 360}, 30%, 40%)`);
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
    
    // Add procedural details
    this.addCraters(ctx, paddedHash, size);
    this.addSurfaceNoise(ctx, paddedHash, size);
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    
    return texture;
  }

  private addCraters(ctx: CanvasRenderingContext2D, hash: string, size: number): void {
    // Ensure hash has sufficient length for substring operations
    const safeHash = hash || 'default_crater_hash';
    const paddedHash = safeHash.length >= 32 ? safeHash : safeHash.padEnd(32, '0');
    
    const craterCount = this.hashToNumber(paddedHash.substring(24, 32), 5, 15);
    
    for (let i = 0; i < craterCount; i++) {
      const x = this.hashToNumber(paddedHash + i + 'x', 0, size);
      const y = this.hashToNumber(paddedHash + i + 'y', 0, size);
      const radius = this.hashToNumber(paddedHash + i + 'r', size * 0.02, size * 0.08);
      
      // Draw crater
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
      gradient.addColorStop(0, 'rgba(0, 0, 0, 0.3)');
      gradient.addColorStop(0.7, 'rgba(0, 0, 0, 0.1)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private addSurfaceNoise(ctx: CanvasRenderingContext2D, _hash: string, size: number): void {
    const imageData = ctx.getImageData(0, 0, size, size);
    const data = imageData.data;
    
    // Add Perlin-like noise
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const idx = (y * size + x) * 4;
        const noise = this.noise2D(x * 0.02, y * 0.02) * 20;
        
        data[idx] = Math.max(0, Math.min(255, data[idx] + noise));
        data[idx + 1] = Math.max(0, Math.min(255, data[idx + 1] + noise));
        data[idx + 2] = Math.max(0, Math.min(255, data[idx + 2] + noise));
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
  }

  private addSurfaceDetails(mesh: THREE.Mesh, hash: string): void {
    const geometry = mesh.geometry as THREE.SphereGeometry;
    const positionAttribute = geometry.attributes['position'];
    const vertex = new THREE.Vector3();
    
    // Ensure hash is defined
    const safeHash = hash || 'default_surface_hash';
    
    // Add slight vertex displacement for surface irregularities
    for (let i = 0; i < positionAttribute.count; i++) {
      vertex.fromBufferAttribute(positionAttribute, i);
      
      // Use hash and vertex position for deterministic displacement
      const displacement = this.hashToNumber(
        safeHash + vertex.x + vertex.y + vertex.z,
        -0.02,
        0.02
      );
      
      vertex.normalize().multiplyScalar(1 + displacement);
      positionAttribute.setXYZ(i, vertex.x, vertex.y, vertex.z);
    }
    
    positionAttribute.needsUpdate = true;
    geometry.computeVertexNormals();
  }

  private hashToNumber(str: string, min: number, max: number): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Normalize to 0-1 range
    const normalized = (hash >>> 0) / 0xFFFFFFFF;
    
    // Scale to desired range
    return min + normalized * (max - min);
  }

  private noise2D(x: number, y: number): number {
    // Simple pseudo-random noise function
    const n = Math.sin(x * 12.9898 + y * 78.233 + this.noiseSeed) * 43758.5453;
    return (n - Math.floor(n)) * 2 - 1;
  }

  public dispose(): void {
    this.baseGeometry.dispose();
  }
}